// Screen-space finish: a faint paper grain and a soft edge vignette. Both are baked
// to small canvas textures once and stretched, so they cost nothing per frame.
import { Container, Sprite, Texture, TilingSprite } from 'pixi.js';

export interface PaperLayer {
  root: Container;
  resize(w: number, h: number): void;
}

export function createPaperLayer(): PaperLayer {
  const root = new Container();
  root.eventMode = 'none';

  const grain = new TilingSprite({ texture: grainTexture(), width: 10, height: 10 });
  grain.alpha = 0.055;
  root.addChild(grain);

  const vignette = new Sprite(vignetteTexture());
  vignette.alpha = 0.5;
  root.addChild(vignette);

  return {
    root,
    resize(w, h) {
      grain.width = w;
      grain.height = h;
      vignette.width = w;
      vignette.height = h;
    },
  };
}

function grainTexture(): Texture {
  const size = 160;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Texture.WHITE;
  const img = ctx.createImageData(size, size);
  let seed = 0x9e3779b9;
  for (let i = 0; i < img.data.length; i += 4) {
    seed = (Math.imul(seed ^ (seed >>> 15), 0x2c1b3c6d) + 0x6d2b79f5) >>> 0;
    const v = 96 + ((seed >>> 8) % 96);
    img.data[i] = v;
    img.data[i + 1] = v;
    img.data[i + 2] = v;
    img.data[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return Texture.from(canvas);
}

function vignetteTexture(): Texture {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Texture.EMPTY;
  const g = ctx.createRadialGradient(
    size / 2,
    size / 2,
    size * 0.32,
    size / 2,
    size / 2,
    size * 0.78,
  );
  g.addColorStop(0, 'rgba(43,42,39,0)');
  g.addColorStop(1, 'rgba(43,42,39,0.16)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return Texture.from(canvas);
}
